import React, { useEffect, useState, useCallback } from "react";
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Typography,
	notification,
} from "antd";
import { CustomSpinner, CustomTable } from "../../utils/CustomComponents";
import dayjs from "dayjs";
import { api } from "../../utils/Api";
import axios from "axios";
const { Title, Text } = Typography;

/**
 ** Options header (columns)
 */
const columns = [
	{
		title: "Date",
		dataIndex: "date",
		key: "date",
		render: (value) => <Text>{value}</Text>,
	},
	{
		title: "User",
		dataIndex: "user",
		key: "user",
		render: (value) => <Text>{value}</Text>,
	},
	{
		title: "Previous Value",
		dataIndex: "prevValue",
		key: "prevValue",
		render: (value) => <Text>{value}</Text>,
	},
	{
		title: "New Value",
		dataIndex: "newValue",
		key: "newValue",
		render: (value) => <Text>{value}</Text>,
	},
];

const Reception = ({ onComplete, isEdit, receptionId }) => {
	const [initialValues, setInitialValues] = useState({});
	const [loading, setLoading] = useState(true);
	const [loadingButton, setLoadingButton] = useState(true);
	const [supplierOptions, setSupplierOptions] = useState([]);
	const [plantOptions, setPlantOptions] = useState([]);
	const [materialOptions, setMaterialOptions] = useState([]);
	const [receptionHistory, setReceptionHistory] = useState([]);
	const [unitOfMeasurement, setUnitOfMeasurement] = useState("");
	const [apiNotification, contextHolder] = notification.useNotification();
	const [form] = Form.useForm();

	useEffect(() => {
		getValues();
	}, []);

	const getValues = async () => {
		await Promise.all([getPlants(), getMaterials(), getSuppliers()]).then(
			() => {
				if (isEdit) {
					getReception();
				} else {
					setInitialValues({
						...initialValues,
						receivedDate: dayjs(),
					});
					setLoading(false);
					setLoadingButton(false);
				}
			}
		);
	};

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			if (data.success) {
				data.payload.map((item) => {
					list.push({
						value: item._id,
						label: item.name,
					});
				});
				setPlantOptions(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const getSuppliers = async () => {
		const url = api + "/supplier/get/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			if (data.success) {
				data.payload.map((item) => {
					list.push({
						value: item._id,
						label: item.name,
					});
				});
				setSupplierOptions(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const getMaterials = async () => {
		const url = api + "/cog/list/inventory/material";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			if (data.success) {
				data.payload.map((item) => {
					list.push({
						value: item._id,
						label: item.material,
						title: item.inventoryUnitOfMeasurement,
					});
				});
				setMaterialOptions(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const getReception = async () => {
		setLoadingButton(true);
		const url = api + `/reception/get/${receptionId}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			if (data.success) {
				const payload = data.payload;
				setInitialValues({
					...initialValues,
					ticket: payload.ticket,
					plant: payload.plant,
					material: {
						value: payload.material._id,
						label: payload.material.material,
						title: payload.material.inventoryUnitOfMeasurement,
					},
					quantity: payload.quantity,
					supplier: payload.supplier,
					receivedDate: dayjs(payload.receivedDate),
					truck: payload.truck,
				});
				setUnitOfMeasurement(payload.material.inventoryUnitOfMeasurement);

				let history = [];
				payload.history.map((item, index) => {
					history.push({
						key: index,
						user: item.user.name,
						field: item.field,
						prevValue: item.prevValue,
						newValue: item.newValue,
						date: new Date(item.createdAt).toLocaleString("en-US", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit",
							hour: "2-digit",
							minute: "2-digit",
							hour12: false,
						}),
					});
				});
				setReceptionHistory(history);

				setLoading(false);
				setLoadingButton(false);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification(
				"error",
				"Error",
				"Something went wrong while obtaining reception"
			);
			console.error(err.message);
			return null;
		}
	};

	const createInventory = async (values) => {
		setLoadingButton(true);

		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const newBody = {
				ticket: values.ticket,
				plant: values.plant,
				material: values.material.value,
				quantity: values.quantity,
				supplier: values.supplier,
				truck: values.truck,
				receivedDate: dayjs(values.receivedDate).toDate(),
			};

			if (isEdit) {
				const url = api + `/reception/update/${receptionId}`;
				const { data } = await axios.patch(url, newBody);
				if (data.success) {
					openNotification("success", "Success", data.message);
					setTimeout(() => {
						setLoadingButton(false);
						onComplete();
					}, 1000);
				} else {
					openNotification("error", "Error", data.message);
					setLoadingButton(false);
				}
			} else {
				const url = api + `/reception/create`;
				newBody.batcher = localStorage.getItem("userId");
				const { data } = await axios.post(url, newBody);
				if (data.success) {
					openNotification("success", "Success", data.message);
					setTimeout(() => {
						setLoadingButton(false);
						onComplete();
					}, 1000);
				} else {
					openNotification("error", "Error", data.message);
					setLoadingButton(false);
				}
			}
		} catch (err) {
			setLoadingButton(false);
			openNotification(
				"error",
				"Error",
				"Something went wrong while creating reception"
			);
			console.error(err.message);
			return null;
		}
	};

	const onChangeMaterial = useCallback(
		(value) => {
			setUnitOfMeasurement(value.title);
		},
		[setUnitOfMeasurement]
	);

	const onFinish = (values) => {
		createInventory(values);
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const CustomForm = () => {
		return (
			<Form
				layout="vertical"
				name="basicForm"
				initialValues={initialValues}
				onFinish={onFinish}
				form={form}
			>
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							label="# Ticket"
							name="ticket"
							rules={[
								{
									required: true,
									message: "Please fill ticket",
								},
							]}
						>
							<Input size="large" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Plant"
							name="plant"
							rules={[
								{
									required: true,
									message: "Please select a plant",
								},
							]}
						>
							<Select
								showSearch
								placeholder={`Select plant`}
								optionFilterProp="children"
								size="large"
								filterOption={filterOption}
								options={plantOptions}
								style={{
									width: "100%",
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Material"
							name="material"
							rules={[
								{
									required: true,
									message: "Please select a material",
								},
							]}
						>
							<Select
								showSearch
								placeholder={`Select material`}
								optionFilterProp="children"
								size="large"
								filterOption={filterOption}
								options={materialOptions}
								style={{
									width: "100%",
								}}
								onChange={onChangeMaterial}
								labelInValue
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Quantity"
							name="quantity"
							rules={[
								{
									required: true,
									message: "Please fill quantity",
								},
							]}
						>
							<InputNumber
								size="large"
								style={{ width: "100%" }}
								formatter={(value) =>
									`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
								}
								parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
								min={0}
								suffix={unitOfMeasurement}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Supplier"
							name="supplier"
							rules={[
								{
									required: true,
									message: "Please select a supplier",
								},
							]}
						>
							<Select
								showSearch
								placeholder={`Select supplier`}
								optionFilterProp="children"
								size="large"
								filterOption={filterOption}
								options={supplierOptions}
								style={{
									width: "100%",
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="# Truck"
							name="truck"
							rules={[
								{
									required: true,
									message: "Please fill truck",
								},
							]}
						>
							<Input size="large" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Received Date"
							name="receivedDate"
							rules={[
								{
									required: true,
									message: "Please select date",
								},
							]}
						>
							<DatePicker
								size="large"
								showTime={{ format: "HH:mm" }}
								format={"MM/DD/YYYY HH:mm"}
								allowClear={false}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
				</Row>
				{isEdit && (
					<>
						<Divider />
						<Title level={3}>History</Title>
						<CustomTable
							loading={loading}
							columns={columns}
							data={receptionHistory}
							isScroll={false}
						/>
					</>
				)}

				<Row gutter={16}>
					<Col>
						<Button
							htmlType="submit"
							type="primary"
							loading={loadingButton}
							size="large"
						>
							Save
						</Button>
					</Col>
				</Row>
			</Form>
		);
	};

	return (
		<>
			{contextHolder}
			{loading ? <CustomSpinner /> : <CustomForm />}
		</>
	);
};

export default Reception;
